import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { phaseColor } from "@/lib/constants";
import { toast } from "sonner";
import { ChevronLeft, Rocket, Loader2, CalendarClock, CalendarCheck, Layers, AlertTriangle, Trash2, Users, Calendar as CalendarIcon, FolderOpen, GraduationCap } from "lucide-react";

const addDays = (iso, n) => {
  const d = new Date(iso + "T12:00:00");
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
};
const fmt = (iso) => (iso ? new Date(iso + "T12:00:00").toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", year: "numeric" }) : "—");

const DateField = ({ id, value, onChange, testid }) => {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" id={id} className={`w-full justify-start gap-2 mt-1.5 font-normal ${!value ? "text-muted-foreground" : ""}`} data-testid={testid}>
          <CalendarIcon className="h-4 w-4 text-muted-foreground" />
          {value ? fmt(value) : "Pick a date"}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar mode="single" selected={value ? new Date(value + "T12:00:00") : undefined}
          defaultMonth={value ? new Date(value + "T12:00:00") : undefined}
          onSelect={(d) => { if (d) { onChange(d.toLocaleDateString("en-CA")); setOpen(false); } }} initialFocus />
      </PopoverContent>
    </Popover>
  );
};

export default function BuildEol() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [eol, setEol] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [projects, setProjects] = useState([]);
  const [projMeta, setProjMeta] = useState(null);
  const [selected, setSelected] = useState("eol");
  const [registry, setRegistry] = useState([]);
  const [name, setName] = useState("");
  const [mode, setMode] = useState("start");
  const [startDate, setStartDate] = useState(new Date().toISOString().slice(0, 10));
  const [endDate, setEndDate] = useState("");
  const [compress, setCompress] = useState(false);
  const [normalize, setNormalize] = useState(false);
  const [showTasks, setShowTasks] = useState(false);
  const [tplTasks, setTplTasks] = useState(null);
  const [industry, setIndustry] = useState("Telecommunications");
  const [owners, setOwners] = useState({});
  const [showOwners, setShowOwners] = useState(false);
  const [loading, setLoading] = useState(false);

  const loadTemplates = () => api.get("/plan-templates").then(({ data }) => setTemplates(data)).catch(() => {});
  useEffect(() => {
    api.get("/templates/eol").then(({ data }) => setEol(data)).catch(() => {});
    api.get("/resource-registry").then(({ data }) => setRegistry(data.map((r) => r.name))).catch(() => {});
    api.get("/projects").then(({ data }) => setProjects(data.filter((p) => !p.archived))).catch(() => {});
    loadTemplates();
  }, []);

  const projId = selected.startsWith("proj:") ? selected.slice(5) : null;
  const tpl = selected === "eol" ? eol : projId ? (projMeta?.id === projId ? projMeta : null) : templates.find((t) => t.id === selected);

  const pickProject = async (pid) => {
    try {
      const { data } = await api.get(`/projects/${pid}/plan-meta`);
      setProjMeta(data);
      setSelected(`proj:${pid}`);
      if (!name.trim()) setName(`${data.name} — rebuilt`);
      if (!data.task_count) toast.warning("That project has no dated tasks to rebuild from.");
    } catch { toast.error("Could not load that project's plan"); }
  };

  const rawSpan = tpl?.span_days ?? 0;
  const velAvg = tpl?.learned ? tpl?.insights?.velocity?.avg : null;
  const span = normalize && velAvg ? Math.max(1, Math.round(rawSpan * velAvg)) : rawSpan;
  const phases = tpl?.phases ?? [];
  const today = new Date().toISOString().slice(0, 10);
  let computedStart = null, computedEnd = null;
  if (mode === "start" && startDate) { computedStart = startDate; computedEnd = span ? addDays(startDate, span) : null; }
  if (mode === "end" && endDate && span) {
    let s = addDays(endDate, -span);
    const dow = new Date(s + "T12:00:00").getDay();
    if (dow === 0) s = addDays(s, -2);
    if (dow === 6) s = addDays(s, -1);
    computedStart = s; computedEnd = endDate;
  }
  const startsInPast = computedStart && computedStart < today;
  const availDays = startsInPast && endDate ? Math.max(0, Math.round((new Date(endDate) - new Date(today)) / 86400000)) : null;
  const factor = compress && availDays != null && span ? Math.min(1, availDays / span) : 1;

  useEffect(() => { setShowTasks(false); setTplTasks(null); }, [selected]);

  const toggleTasks = async () => {
    if (showTasks) { setShowTasks(false); return; }
    setShowTasks(true);
    if (selected === "eol") setTplTasks(eol?.tasks || []);
    else if (projId) setTplTasks(projMeta?.tasks || []);
    else {
      try {
        const { data } = await api.get(`/plan-templates/${selected}`);
        setTplTasks(data.tasks || []);
      } catch { setTplTasks([]); toast.error("Could not load the task list"); }
    }
  };

  const deleteTemplate = async (t) => {
    if (!window.confirm(`Delete the "${t.name}" template?`)) return;
    try {
      await api.delete(`/plan-templates/${t.id}`);
      if (selected === t.id) setSelected("eol");
      loadTemplates();
      toast.success("Template deleted");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not delete"); }
  };

  const build = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        name,
        start_date: mode === "start" ? startDate : null,
        end_date: mode === "end" ? endDate : null,
        compress: mode === "end" && startsInPast && compress,
        normalize: !!(velAvg && normalize),
        owners: Object.fromEntries(Object.entries(owners).filter(([, v]) => (v || "").trim())),
      };
      const url = selected === "eol" ? "/templates/eol/build" : projId ? `/projects/${projId}/rebuild-plan` : `/plan-templates/${selected}/build`;
      if (selected === "eol") payload.industry = industry;
      const { data } = await api.post(url, payload);
      if (data.normalized) toast.info(`Durations normalized to standard pace (${data.normalized}× team velocity)`, { duration: 6000 });
      if (data.compressed) toast.success(`Plan built compressed to ${Math.round((data.factor || 1) * 100)}% pace — fits by ${fmt(data.computed_end)}`, { duration: 8000 });
      else if (data.mode === "backward") toast.success(`Plan built — work needs to start by ${fmt(data.computed_start)} to hit your target`, { duration: 8000 });
      else toast.success(`Plan built — projected finish ${fmt(data.computed_end)}`);
      nav(`/projects/${data.id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Build failed");
    } finally {
      setLoading(false);
    }
  };

  const phaseCounts = selected === "eol" && eol
    ? eol.phases.map((p) => ({ phase: p, count: eol.tasks.filter((t) => t.phase === p).length }))
    : phases.map((p) => ({ phase: p, count: null }));

  return (
    <Layout>
      <button onClick={() => nav("/")} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4" data-testid="back-to-projects">
        <ChevronLeft className="h-4 w-4" /> Projects
      </button>

      <div className="mb-8">
        <p className="overline text-muted-foreground mb-2">Preliminary planning, fast</p>
        <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight">Plan Wizard</h1>
        <p className="text-muted-foreground mt-2 max-w-2xl">Pick a template — or one of your existing projects — then give it a start date (build forward) or a target end date (work backward — it tells you when work needs to start). Save any project's plan as a template from its WBS toolbar.</p>
      </div>

      <div className="mb-6">
        <Label className="mb-2 block">Template</Label>
        <div className="flex flex-wrap gap-2" data-testid="template-picker">
          <button type="button" onClick={() => setSelected("eol")}
            className={`px-3.5 py-2 rounded-lg border-2 text-sm font-semibold transition-all ${selected === "eol" ? "border-primary bg-primary/5" : "border-border hover:border-foreground/30"}`}
            data-testid="template-eol">
            EOL Framework <span className="text-[11px] font-normal text-muted-foreground">built-in · {eol?.task_count ?? "…"} tasks · ≈{eol?.span_weeks ?? "…"} wks</span>
          </button>
          {templates.map((t) => {
            const canDelete = user && (t.owner_id === user.id || user.role === "admin");
            return (
            <span key={t.id} className={`inline-flex items-center gap-1 rounded-lg border-2 transition-all ${selected === t.id ? "border-primary bg-primary/5" : "border-border hover:border-foreground/30"}`}>
              <button type="button" onClick={() => setSelected(t.id)} className={`pl-3.5 py-2 text-sm font-semibold ${canDelete ? "" : "pr-3.5"}`} data-testid={`template-${t.id}`}>
                {t.learned && <GraduationCap className="h-3.5 w-3.5 inline -mt-0.5 mr-1 text-emerald-600" title="Learned template — built from real results" data-testid={`template-learned-badge-${t.id}`} />}
                {t.name} <span className="text-[11px] font-normal text-muted-foreground">{t.task_count} tasks · ≈{t.span_weeks} wks{t.owner_name && (!user || t.owner_id !== user.id) ? ` · by ${t.owner_name}` : ""}</span>
              </button>
              {canDelete && (
                <button type="button" onClick={() => deleteTemplate(t)} className="pr-2.5 pl-1 py-2 text-muted-foreground hover:text-destructive" title="Delete template" data-testid={`template-delete-${t.id}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              )}
            </span>
            );
          })}
          {projects.length > 0 && (
            <Select value={projId || ""} onValueChange={pickProject}>
              <SelectTrigger className={`w-auto min-w-[230px] h-auto py-2 rounded-lg border-2 text-sm font-semibold transition-all ${projId ? "border-primary bg-primary/5" : "border-border hover:border-foreground/30"}`} data-testid="rebuild-project-select">
                <span className="flex items-center gap-1.5 pr-1">
                  <FolderOpen className={`h-3.5 w-3.5 ${projId ? "text-primary" : "text-muted-foreground"}`} />
                  <SelectValue placeholder="Rebuild an existing project…" />
                </span>
              </SelectTrigger>
              <SelectContent>
                {projects.map((p) => <SelectItem key={p.id} value={p.id} data-testid={`rebuild-project-${p.id}`}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          {templates.length === 0 && <span className="text-xs text-muted-foreground self-center">No saved templates yet — open any project's WBS and click "Save as template". Saved templates are shared with your teammates.</span>}
        </div>
      </div>

      <div className="grid lg:grid-cols-5 gap-8">
        <form onSubmit={build} className="lg:col-span-2 border border-border rounded-lg p-6 h-fit" data-testid="build-form">
          <div className="space-y-4">
            <div>
              <Label htmlFor="b-name">Plan name</Label>
              <Input id="b-name" required value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Product X End of Life — 2027" data-testid="build-name-input" className="mt-1.5" />
            </div>
            <div>
              <Label>How do you want to schedule it?</Label>
              <div className="grid grid-cols-2 gap-2 mt-1.5" data-testid="build-mode-toggle">
                <button type="button" onClick={() => setMode("start")} className={`flex flex-col items-start gap-0.5 rounded-lg border-2 px-3 py-2.5 text-left transition-all ${mode === "start" ? "border-primary bg-primary/5" : "border-border hover:border-foreground/30"}`} data-testid="build-mode-start">
                  <span className="flex items-center gap-1.5 text-sm font-bold"><CalendarClock className={`h-4 w-4 ${mode === "start" ? "text-primary" : "text-muted-foreground"}`} /> I know the start</span>
                  <span className="text-[11px] text-muted-foreground">Build the plan forward</span>
                </button>
                <button type="button" onClick={() => setMode("end")} className={`flex flex-col items-start gap-0.5 rounded-lg border-2 px-3 py-2.5 text-left transition-all ${mode === "end" ? "border-primary bg-primary/5" : "border-border hover:border-foreground/30"}`} data-testid="build-mode-end">
                  <span className="flex items-center gap-1.5 text-sm font-bold"><CalendarCheck className={`h-4 w-4 ${mode === "end" ? "text-primary" : "text-muted-foreground"}`} /> I have an end date</span>
                  <span className="text-[11px] text-muted-foreground">Work backward from it</span>
                </button>
              </div>
            </div>
            {mode === "start" ? (
              <div>
                <Label htmlFor="b-start">Planning start date</Label>
                <DateField id="b-start" value={startDate} onChange={setStartDate} testid="build-start-input" />
              </div>
            ) : (
              <div>
                <Label htmlFor="b-end">Target end date</Label>
                <DateField id="b-end" value={endDate} onChange={setEndDate} testid="build-end-input" />
              </div>
            )}
            {velAvg != null && velAvg !== 1 && (
              <label className="flex items-start gap-2.5 rounded-lg border border-emerald-200 bg-emerald-50/60 p-3 cursor-pointer" data-testid="build-normalize-toggle">
                <Checkbox checked={normalize} onCheckedChange={(v) => setNormalize(!!v)} className="mt-0.5" data-testid="build-normalize-checkbox" />
                <span className="text-xs">
                  <span className="text-sm font-bold flex items-center gap-1.5"><GraduationCap className="h-3.5 w-3.5 text-emerald-600" /> Normalize to standard pace</span>
                  <span className="text-muted-foreground">This template's team ran at {velAvg}× velocity. {velAvg < 1 ? "Shorten" : "Lengthen"} durations for a standard-pace team — ≈ {Math.round((rawSpan * velAvg / 7) * 10) / 10} wks instead of {Math.round((rawSpan / 7) * 10) / 10} wks. Off = use the actual durations as they really happened.</span>
                </span>
              </label>
            )}
            {computedStart && computedEnd && (
              <div className={`rounded-lg border px-3.5 py-3 text-sm ${startsInPast && !compress ? "border-red-300 bg-red-50" : "border-primary/30 bg-primary/5"}`} data-testid="build-schedule-preview">
                {mode === "end" ? (
                  startsInPast && compress ? (
                    <p>Compressed plan: starts <b>{fmt(today)}</b> and fits by <b>{fmt(endDate)}</b> at <b data-testid="build-compress-factor">{Math.round(factor * 100)}%</b> of the template pace.</p>
                  ) : (
                    <p className={startsInPast ? "text-red-700" : ""}>
                      {startsInPast && <AlertTriangle className="h-4 w-4 inline mr-1 -mt-0.5" />}
                      Work needs to start by <b data-testid="build-computed-start">{fmt(computedStart)}</b> to finish by <b>{fmt(computedEnd)}</b>{startsInPast ? " — that's in the past." : "."}
                    </p>
                  )
                ) : (
                  <p>Starting <b>{fmt(computedStart)}</b>, the plan projects to finish by <b data-testid="build-computed-end">{fmt(computedEnd)}</b>.</p>
                )}
                <p className="text-[11px] text-muted-foreground mt-1">≈ {Math.round((span / 7) * 10) / 10} weeks end-to-end · {tpl?.task_count} preset tasks with durations & dependencies</p>
                {startsInPast && (
                  <label className="flex items-center gap-2 mt-2 cursor-pointer" data-testid="build-compress-toggle">
                    <Checkbox checked={compress} onCheckedChange={(v) => setCompress(!!v)} data-testid="build-compress-checkbox" />
                    <span className="text-xs font-semibold">Compress the schedule to fit the remaining time</span>
                  </label>
                )}
                {startsInPast && compress && factor < 0.5 && (
                  <p className="text-xs text-red-700 font-semibold mt-2 flex items-start gap-1.5" data-testid="build-compress-sanity-warning">
                    <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" /> Heads-up: that's under half the template's normal pace — this timeline may be unrealistic. Consider trimming scope or moving the target date.
                  </p>
                )}
              </div>
            )}
            {selected === "eol" && (
              <div>
                <Label htmlFor="b-ind">Industry</Label>
                <Input id="b-ind" value={industry} onChange={(e) => setIndustry(e.target.value)} data-testid="build-industry-input" className="mt-1.5" />
              </div>
            )}
            {phases.length > 0 && (
              <div className="border border-border rounded-lg p-3">
                <button type="button" onClick={() => setShowOwners((v) => !v)} className="flex items-center gap-1.5 text-sm font-bold w-full" data-testid="build-owners-toggle">
                  <Users className="h-4 w-4 text-primary" /> Stage owners <span className="text-[11px] font-normal text-muted-foreground">(optional — prefills assignees per stage)</span>
                  <span className="ml-auto text-muted-foreground">{showOwners ? "−" : "+"}</span>
                </button>
                {showOwners && (
                  <div className="mt-3 space-y-2" data-testid="build-owners-list">
                    {phases.map((p) => (
                      <div key={p} className="flex items-center gap-2">
                        <span className="text-xs font-semibold w-36 shrink-0 truncate" title={p}>{p}</span>
                        <Input value={owners[p] || ""} onChange={(e) => setOwners((o) => ({ ...o, [p]: e.target.value }))} placeholder="Owner name" list="wizard-owner-options" className="h-8 text-sm" data-testid={`build-owner-${p}`} />
                      </div>
                    ))}
                    <datalist id="wizard-owner-options">{registry.map((n) => <option key={n} value={n} />)}</datalist>
                  </div>
                )}
              </div>
            )}
          </div>
          <Button type="submit" disabled={loading || !name.trim() || (mode === "end" && !endDate) || !tpl || (projId && !tpl?.task_count)} className="w-full mt-6 h-11 gap-2" data-testid="build-submit-button">
            {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Building...</> : <><Rocket className="h-4 w-4" /> Build plan</>}
          </Button>
        </form>

        <div className="lg:col-span-3 border border-border rounded-lg p-6" data-testid="framework-preview">
          <div className="flex items-center gap-2 mb-1">
            <Layers className="h-5 w-5 text-primary" />
            <h2 className="font-display text-xl font-bold">{selected === "eol" ? "EOL Framework template" : projId ? `Rebuild: ${projMeta?.name || ""}` : tpl?.name || "Template"}</h2>
          </div>
          <p className="text-sm text-muted-foreground mb-5">
            {tpl ? `${tpl.task_count} tasks across ${phases.length} stages · ≈ ${tpl.span_weeks} weeks${projId ? " · a rescheduled copy — the original project stays untouched" : selected !== "eol" && tpl.source_project ? ` · from "${tpl.source_project}"` : ""}` : "Loading template…"}
          </p>
          {tpl?.learned && tpl?.insights && (
            <div className="mb-5 rounded-lg border border-emerald-200 bg-emerald-50/60 p-3.5" data-testid="learned-insights-panel">
              <p className="text-xs font-bold text-emerald-800 flex items-center gap-1.5 mb-1.5"><GraduationCap className="h-4 w-4" /> Learned from real results — durations reflect what actually happened</p>
              <ul className="text-xs text-emerald-900 space-y-1">
                {tpl.insights.overall && (
                  <li data-testid="learned-insight-overall">Overall: baselined {tpl.insights.overall.planned_days}d → actually took <b>{tpl.insights.overall.actual_days}d</b> ({tpl.insights.overall.variance_pct >= 0 ? "+" : ""}{tpl.insights.overall.variance_pct}% vs plan)</li>
                )}
                {(tpl.insights.per_phase || []).filter((p) => Math.abs(p.variance_pct) >= 5).slice(0, 5).map((p) => (
                  <li key={p.phase}>{p.phase}: {p.planned_days}d planned → {p.actual_days}d actual ({p.variance_pct >= 0 ? "+" : ""}{p.variance_pct}%)</li>
                ))}
                {tpl.insights.velocity?.avg != null && (
                  <li data-testid="learned-insight-velocity">Team velocity {tpl.insights.velocity.avg}× — this template already runs at that team's real pace</li>
                )}
                <li className="text-emerald-700/80">{tpl.insights.done_count}/{tpl.insights.task_count} tasks were complete when this template was learned</li>
                {(tpl.relearn_count || 0) > 0 && (
                  <li className="font-semibold" data-testid="learned-insight-relearn">Re-learned {tpl.relearn_count} time{tpl.relearn_count === 1 ? "" : "s"} — estimates refined from {tpl.relearn_count + 1} completed projects</li>
                )}
              </ul>
            </div>
          )}
          {tpl && (
            <div className="mb-5">
              <button type="button" onClick={toggleTasks} className="text-sm font-semibold text-primary hover:underline inline-flex items-center gap-1" data-testid="preview-tasks-toggle">
                {showTasks ? "Hide task list" : `View all ${tpl.task_count} tasks`}
              </button>
              {showTasks && (
                <div className="mt-2 rounded-lg border border-border max-h-80 overflow-y-auto divide-y divide-border/70" data-testid="preview-tasks-list">
                  {phases.map((ph, pi) => {
                    const rows = (tplTasks || []).filter((t) => (t.phase || "General") === ph);
                    if (!rows.length) return null;
                    return (
                      <div key={ph} className="px-3 py-2">
                        <p className="text-[11px] font-bold uppercase tracking-wide mb-1" style={{ color: phaseColor(ph, pi) }}>{ph}</p>
                        {rows.map((t, i) => (
                          <div key={`${ph}-${i}`} className="flex items-center gap-2 py-0.5 text-xs">
                            <span className="flex-1 truncate">{t.is_milestone ? "◆ " : ""}{t.title}</span>
                            {t.assignee ? <span className="text-muted-foreground truncate max-w-[110px]">{t.assignee}</span> : t.team ? <span className="text-muted-foreground truncate max-w-[110px]">{t.team}</span> : null}
                            <span className="text-muted-foreground shrink-0 w-16 text-right">
                              {t.is_milestone ? "milestone" : t.end_week != null ? `wk ${t.start_week}–${t.end_week}` : `${Math.max(1, Math.round((t.duration_days || 0) * (normalize && velAvg ? velAvg : 1)))}d`}
                            </span>
                          </div>
                        ))}
                      </div>
                    );
                  })}
                  {tplTasks && !tplTasks.length && <p className="p-3 text-xs text-muted-foreground">No tasks found.</p>}
                  {!tplTasks && <p className="p-3 text-xs text-muted-foreground">Loading tasks…</p>}
                </div>
              )}
            </div>
          )}
          <div className="grid sm:grid-cols-2 gap-3">
            {phaseCounts.map((pc, i) => (
              <div key={pc.phase} className="border border-border rounded-md p-4 flex items-center justify-between">
                <div className="flex items-center gap-2.5 min-w-0">
                  <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: phaseColor(pc.phase, i) }} />
                  <span className="font-medium text-sm truncate">{pc.phase}</span>
                </div>
                {pc.count != null && <span className="text-xs text-muted-foreground shrink-0">{pc.count} tasks</span>}
              </div>
            ))}
          </div>
        </div>
      </div>
    </Layout>
  );
}
